import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import StatusBadge from './StatusBadge';

const InventoryTable = ({ 
  units, 
  onMarkVacant, 
  onMarkOccupied, 
  onAssignTenant,
  onUpdatePrice,
  onAddNote,
  selectedUnits,
  onSelectUnit,
  onSelectAll
}) => {
  const [expandedRow, setExpandedRow] = useState(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    })?.format(amount);
  };

  const getUnitTypeLabel = (type, coworkingSeatType) => {
    const labels = {
      coworking: 'Coworking',
      bareshell: 'Bare Shell',
      managed_office: 'Managed Office',
      furnished: 'Furnished'
    };

    const coworkingSeatLabels = {
      hot_desk: 'Hot Desk',
      fixed_desk: 'Fixed Desk',
      manager_cabin: 'Manager Cabin',
      team_suite_with_manager: 'Team Suite (w/ Manager)',
      team_suite_without_manager: 'Team Suite'
    };

    // For coworking units, show the specific seat type
    if (type === 'coworking' && coworkingSeatType) {
      return coworkingSeatLabels?.[coworkingSeatType] || labels?.[type];
    }

    return labels?.[type] || type;
  };

  const getPricingDisplay = (unit) => {
    switch (unit?.pricingModel) {
      case 'per_seat':
        return {
          label: `₹${unit?.pricePerSeat?.toLocaleString('en-IN')}/seat`,
          capacity: `${unit?.capacitySeats} seats`,
          total: (unit?.capacitySeats || 0) * (unit?.pricePerSeat || 0)
        };
      case 'per_sqft':
        return {
          label: `₹${unit?.pricePerSqft?.toLocaleString('en-IN')}/sqft`,
          capacity: `${unit?.capacitySqft || unit?.areaSqft} sqft`,
          total: (unit?.capacitySqft || unit?.areaSqft || 0) * (unit?.pricePerSqft || 0)
        };
      case 'fixed':
        return {
          label: 'Fixed',
          capacity: `${unit?.capacitySqft || unit?.areaSqft} sqft`,
          total: unit?.fixedMonthlyPrice || 0
        };
      default:
        return {
          label: '—',
          capacity: '—',
          total: 0
        };
    }
  };

  const toggleRow = (unitId) => {
    setExpandedRow(expandedRow === unitId ? null : unitId);
  };

  const allSelected = units?.length > 0 && selectedUnits?.length === units?.length;

  return (
    <>
      {/* Desktop Table View */}
      <div className="hidden lg:block bg-card rounded-lg border border-border shadow-elevation-2 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-muted">
              <tr>
                <th className="px-3 py-3 text-left w-12">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    onChange={(e) => onSelectAll(e?.target?.checked)}
                    className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                  />
                </th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Unit #</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Floor</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Type</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Capacity</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Pricing</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Status</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Tenant</th>
                <th className="px-3 py-3 text-left text-sm font-semibold text-foreground">Revenue/Month</th>
                <th className="px-3 py-3 text-right text-sm font-semibold text-foreground min-w-[200px]">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {units?.map((unit) => {
                const pricingDisplay = getPricingDisplay(unit);
                return (
                  <tr key={unit?.id} className="hover:bg-muted hover:bg-opacity-50 transition-smooth">
                    <td className="px-3 py-4">
                      <input
                        type="checkbox"
                        checked={selectedUnits?.includes(unit?.id)}
                        onChange={(e) => onSelectUnit(unit?.id, e?.target?.checked)}
                        className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary"
                      />
                    </td>
                    <td className="px-3 py-4 text-sm font-medium text-foreground">{unit?.unitNumber}</td>
                    <td className="px-3 py-4 text-sm text-foreground">Floor {unit?.floor}</td>
                    <td className="px-3 py-4 text-sm text-foreground">{getUnitTypeLabel(unit?.unitType, unit?.coworkingSeatType)}</td>
                    <td className="px-3 py-4 text-sm text-foreground">{pricingDisplay?.capacity}</td>
                    <td className="px-3 py-4 text-sm text-foreground">{pricingDisplay?.label}</td>
                    <td className="px-3 py-4">
                      <StatusBadge status={unit?.status} />
                    </td>
                    <td className="px-3 py-4 text-sm">
                      {unit?.tenant ? (
                        <Link 
                          to={`/tenant-management?id=${unit?.tenantId}`}
                          className="text-primary hover:underline font-medium"
                        >
                          {unit?.tenant}
                        </Link>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm font-medium text-foreground">
                      {formatCurrency(pricingDisplay?.total)}
                    </td>
                    <td className="px-3 py-4">
                      <div className="flex items-center justify-end gap-1.5 flex-wrap">
                        {unit?.status === 'occupied' && (
                          <Button
                            variant="outline"
                            size="sm"
                            iconName="UserX"
                            onClick={() => onMarkVacant(unit?.id)}
                          >
                            Mark Vacant
                          </Button>
                        )}
                        {unit?.status === 'vacant' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              iconName="UserCheck"
                              onClick={() => onMarkOccupied(unit?.id)}
                            >
                              Mark Occupied
                            </Button>
                            <Button
                              variant="default"
                              size="sm"
                              iconName="UserPlus"
                              onClick={() => onAssignTenant(unit)}
                            >
                              Assign Tenant
                            </Button>
                          </>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="DollarSign"
                          onClick={() => onUpdatePrice(unit)}
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          iconName="FileText"
                          onClick={() => onAddNote(unit)}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
      {/* Mobile Card View */}
      <div className="lg:hidden space-y-3">
        {units?.map((unit) => {
          const pricingDisplay = getPricingDisplay(unit);
          return (
            <div key={unit?.id} className="bg-card rounded-lg border border-border shadow-elevation-2 overflow-hidden">
              <div className="p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <input
                      type="checkbox"
                      checked={selectedUnits?.includes(unit?.id)}
                      onChange={(e) => onSelectUnit(unit?.id, e?.target?.checked)}
                      className="mt-1 w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary flex-shrink-0"
                    />
                    <div className="flex-1 min-w-0">
                      <h3 className="text-base font-semibold text-foreground mb-1">
                        Unit {unit?.unitNumber}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        Floor {unit?.floor} • {getUnitTypeLabel(unit?.unitType, unit?.coworkingSeatType)}
                      </p>
                    </div>
                  </div>
                  <StatusBadge status={unit?.status} />
                </div>

                <div className="grid grid-cols-2 gap-3 mb-3">
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Capacity</p>
                    <p className="text-sm font-medium text-foreground">{pricingDisplay?.capacity}</p>
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground mb-1">Pricing</p>
                    <p className="text-sm font-medium text-foreground">{pricingDisplay?.label}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-xs text-muted-foreground mb-1">Revenue/Month</p>
                    <p className="text-sm font-medium text-foreground">{formatCurrency(pricingDisplay?.total)}</p>
                  </div>
                </div>

                {unit?.tenant && (
                  <div className="mb-3">
                    <p className="text-xs text-muted-foreground mb-1">Tenant</p>
                    <Link 
                      to={`/tenant-management?id=${unit?.tenantId}`}
                      className="text-sm text-primary hover:underline font-medium"
                    >
                      {unit?.tenant}
                    </Link>
                  </div>
                )}

                <button
                  onClick={() => toggleRow(unit?.id)}
                  className="w-full flex items-center justify-center gap-2 py-2 text-sm text-primary hover:bg-primary hover:bg-opacity-5 rounded-lg transition-smooth"
                >
                  <span>Actions</span>
                  <Icon 
                    name={expandedRow === unit?.id ? "ChevronUp" : "ChevronDown"} 
                    size={16} 
                  />
                </button>
              </div>

              {expandedRow === unit?.id && (
                <div className="border-t border-border p-4 bg-muted bg-opacity-30 space-y-2">
                  {unit?.status === 'occupied' && (
                    <Button
                      variant="outline"
                      size="sm"
                      iconName="UserX"
                      fullWidth
                      onClick={() => onMarkVacant(unit?.id)}
                    >
                      Mark Vacant
                    </Button>
                  )}
                  {unit?.status === 'vacant' && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        iconName="UserCheck"
                        fullWidth
                        onClick={() => onMarkOccupied(unit?.id)}
                      >
                        Mark Occupied
                      </Button>
                      <Button
                        variant="default"
                        size="sm"
                        iconName="UserPlus"
                        fullWidth
                        onClick={() => onAssignTenant(unit)}
                      >
                        Assign Tenant
                      </Button>
                    </>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="DollarSign"
                    fullWidth
                    onClick={() => onUpdatePrice(unit)}
                  >
                    Update Pricing
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    iconName="FileText"
                    fullWidth
                    onClick={() => onAddNote(unit)}
                  >
                    Add Note
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </>
  );
};

export default InventoryTable;